import * as React from 'react';
import {
  DataGridPremium,
  GridFilterItem,
  GridFilterModel,
} from '@mui/x-data-grid-premium';
import { Alert, Tooltip, Typography } from '@mui/material';
import { formatDistance } from 'date-fns';
import { formatTime } from './flows/utils';
import { useGlobalSettings } from '../app/GlobalSettingProvider';
import { useDataGridUrlSync } from './useDataGridUrlSync';
import { useHighlightRowByUrl } from './highlightRowByUrl';
import { useEffect } from 'react';

// Type for a single sort item in the sort model
export type SortModelItem = { field: string; sort?: 'asc' | 'desc' };

// Type for the query param state
export interface AQDataGridQueryParams {
  size: number;
  page: number;
  sort?: SortModelItem[];
  filter?: GridFilterItem[];
  tab?: string;
}

// Props for AQDataGrid
export interface AQDataGridProps {
  id?: string;
  dataQueryHook?: (...args: any[]) => any; // The hook to use (e.g., useEntities, useOtherQuery)
  buildQueryArgs?: (queryParams: AQDataGridQueryParams) => any[]; // Function to build hook args
  rowsTransformer?: (rows: any[]) => any[];
  rows?: any[];
  colDefs: any[];
  dataDependencies?: Array<any>;
  sx?: any;
  gridProps?: any;
  customErrorElement?: React.ReactNode;
  loading?: boolean;
}

const AQDataGrid = ({
  id,
  dataQueryHook,
  buildQueryArgs,
  rowsTransformer,
  rows,
  colDefs,
  dataDependencies = [],
  sx = {},
  gridProps = {},
  loading,
  customErrorElement,
}: AQDataGridProps) => {
  const { settings } = useGlobalSettings();
  const formatInTimeZone = formatTime(settings.timeZone.current);

  // sync URL/query param
  const { queryParams, setQueryParams } = useDataGridUrlSync();

  // Build the arguments for the hook
  const hookArgs = React.useMemo(
    () => buildQueryArgs?.(queryParams) || [],
    [buildQueryArgs, queryParams],
  );

  // Call the hook at the top level
  const query = dataQueryHook?.(...hookArgs);

  const depsErrored = dataDependencies.some((dep) => Boolean(dep?.isError));
  let errorEl = null;
  if (query?.isError || depsErrored) {
    if (window.location.search.includes('debug')) {
      console.error('AQDataGrid: Error fetching data', query?.error);
      console.error(
        'Error fetching data',
        dataDependencies.find((dep) => dep?.isError)?.error,
      );
    }
    errorEl = customErrorElement || (
      <Alert sx={{ maxWidth: '1200px', margin: '24px auto' }} severity="error">
        Error Fetching Table Data
      </Alert>
    );
  }

  const internalRows = React.useMemo(() => {
    if (rows) return rowsTransformer ? rowsTransformer(rows) : rows;

    let rowsData = [];
    if (query?.data?.getData?.()) {
      rowsData = query?.data.getData() || [];
    } else {
      rowsData = query.data || [];
    }
    if (rowsTransformer) {
      return rowsTransformer(rowsData);
    }
    return rowsData;
  }, [query?.data, rowsTransformer, rows]);

  if (loading === undefined) {
    loading = [...dataDependencies, query].some((dep) => dep?.isLoading);
  } else {
    loading = Boolean(loading);
  }

  // Use the new hook for row highlight by URL
  const { matchRowByHighlight, selectedRowId } =
    useHighlightRowByUrl(internalRows);

  // @ts-expect-error: sortModel is not typed from DataGridPremium
  const handleSortModelChange = React.useCallback((sortModel) => {
    setQueryParams((params) => ({
      ...params,
      sort: sortModel,
    }));
  }, []);

  const handleFilterModelChange = React.useCallback(
    (filterModel: GridFilterModel) => {
      setQueryParams((params) => ({
        ...params,
        filter: filterModel.items || [],
      }));
    },
    [],
  );

  const handlePaginationModelChange = React.useCallback(
    (paginationModel: any) => {
      setQueryParams((params) => {
        const newPage = paginationModel.page + 1;
        const newSize = paginationModel.pageSize;

        if (params.page !== newPage || params.size !== newSize) {
          return {
            ...params,
            page: newPage,
            size: newSize,
          };
        }
        return params;
      });
    },
    [],
  );

  let datumCount = 0;
  if (query?.data?.meta?.count !== undefined) {
    datumCount = query.data.meta.count;
  } else if (query?.data?.getData?.()) {
    datumCount = query.data.getData().length;
  } else {
    datumCount = internalRows.length;
  }

  const rowCountRef = React.useRef(datumCount || 0);

  const rowCount = React.useMemo(() => {
    if (datumCount !== undefined) {
      rowCountRef.current = datumCount;
    }
    return rowCountRef.current;
  }, [datumCount]);

  const paginationModel = {
    page: queryParams.page === 1 ? 0 : queryParams.page - 1,
    pageSize: queryParams.size || 10,
  };

  if (gridProps.pinnedRows) {
    if (gridProps.pinnedRows.topFilter) {
      const topRows = (internalRows || []).filter(
        gridProps.pinnedRows.topFilter,
      );
      gridProps.pinnedRows.top = topRows;
    }
    if (gridProps.pinnedRows.bottomFilter) {
      const bottomRows = (internalRows || []).filter(
        gridProps.pinnedRows.bottomFilter,
      );
      gridProps.pinnedRows.bottom = bottomRows;
    }
  }

  return (
    <div
      id={`aq-datagrid-${id || ''}`}
      style={{
        width: '100%',
        maxWidth: '1200px',
        margin: '0 auto',
        ...sx?.container,
      }}
    >
      {!query?.isError && !depsErrored && (
        <Typography
          variant="overline"
          color="textSecondary"
          sx={{ display: 'inline-block', marginBottom: 0, ...sx?.refreshText }}
        >
          <Tooltip
            title={
              query?.dataUpdatedAt
                ? formatInTimeZone(query.dataUpdatedAt)
                : formatInTimeZone(new Date())
            }
          >
            <small>
              Table Data Refreshed :{' '}
              {formatDistance(query?.dataUpdatedAt || new Date(), new Date(), {
                addSuffix: true,
              })}
              <br />
            </small>
          </Tooltip>
        </Typography>
      )}
      {errorEl || (
        <DataGridPremium
          data-testid="aq-datagrid"
          key={`${paginationModel.page}-${paginationModel.pageSize}`}
          pagination={true}
          rows={internalRows}
          sortingMode={query ? 'server' : 'client'}
          paginationMode={query ? 'server' : 'client'}
          disableAggregation={true}
          filterMode={query ? 'server' : 'client'}
          {...(query && { rowCount })}
          columns={colDefs}
          loading={loading}
          rowSelectionModel={selectedRowId ? [selectedRowId] : []}
          paginationModel={paginationModel}
          filterModel={{
            items: queryParams.filter,
          }}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[5, 10, 25, 50]}
          sx={{
            boxShadow: '5px 0 5px rgba(173, 216, 230, 0.25)',
            '& .MuiDataGrid-columnHeaderTitle': {
              fontWeight: 'bold',
            },
            ...sx,
          }}
          {...gridProps}
          getRowClassName={(params) => {
            let baseClass = 'aq-datagrid-row';
            if (matchRowByHighlight(params.row)) {
              baseClass += ' aq-datagrid-row--highlighted';
            }
            if (gridProps.getRowClassName) {
              baseClass += ' ' + gridProps.getRowClassName(params);
            }
            return baseClass;
          }}
          slotProps={{
            loadingOverlay: {
              variant: 'skeleton',
              noRowsVariant: 'skeleton',
            },
            ...gridProps?.slots,
          }}
        />
      )}
    </div>
  );
};

export default AQDataGrid;
